import Preferences, { hasFullScope } from "context/preferences";

// The saved choice, at blob scope. The default is remembered as the ABSENCE of this key, never as its id.
export const ACTIVE_REVISION_KEY = "blob.activeRevision";

/**
 * The saved revision id for this blob, or null. Read at the blob's own level, never through the cascade.
 */
export function rememberedRevision(scope) {
  if (!hasFullScope(scope)) return null;
  return claimed(Preferences.getRaw(scope)[ACTIVE_REVISION_KEY]);
}

/** Writes back `remember` ("set", "clear" or "none"). Refuses a partial scope, which would write for every blob. */
export function applyRevisionMemory(scope, outcome) {
  if (!hasFullScope(scope) || !outcome) return;
  if (outcome.remember === "set") {
    Preferences.set(ACTIVE_REVISION_KEY, outcome.revisionId, scope);
  } else if (outcome.remember === "clear") {
    Preferences.remove(ACTIVE_REVISION_KEY, scope);
  }
}

// What a failed fetch says about the revision: "gone" (404), "forbidden" (403 -- this reader may not
// open it), or "transient" -- every other failure, which says nothing about the revision at all.
export function revisionFetchFailure(error) {
  const status = error?.response?.status;
  if (404 === status) return "gone";
  if (403 === status) return "forbidden";
  return "transient";
}

/** A revision id somebody claims — from the store or the URL. Anything else is not one. */
function claimed(value) {
  return typeof value === "string" && value ? value : null;
}

// The revisions arrival tries, in order and each at most once: a link's claim, the saved choice, then the
// default (docs/specs/2026-09-18-revision-selection.md#arrival-refresh-and-the-one-door).
export function arrivalCandidates({ claimedId, storedId, defaultRevisionId } = {}) {
  const candidates = [];
  for (const [id, from] of [[claimedId, "claim"], [storedId, "saved"], [defaultRevisionId, "default"]]) {
    if (claimed(id) && !candidates.some(candidate => candidate.id === id)) {
      candidates.push({ id, from });
    }
  }
  return candidates;
}

// What the saved choice becomes once `candidate` has loaded. A link's claim and a pick in the selector are
// the only choices, and picking the default is the only thing that clears one.
export function memoryOnLoad(candidate, defaultRevisionId) {
  if ("claim" === candidate.from) return "set";
  if ("selected" === candidate.from) return candidate.id === defaultRevisionId ? "clear" : "set";
  return "none";
}

// Only an answer that says the saved revision is gone, or closed to this reader, forgets it.
export function memoryOnFailure(revisionId, reason, storedId) {
  return "transient" !== reason && revisionId === storedId ? "clear" : "none";
}
